<template>
  <BitButton v-if="!isActionWrapper" :class="$style.bannerButton" @click="openLink">
    <slot />
  </BitButton>
  <div v-else :class="$style.bannerActionWrapper" @click="openLink">
    <slot />
  </div>
</template>

<script lang="ts" setup>
import { BitButton } from '@bit/ui-kit';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStoreRoot } from '~/stores/abstract/root';
import { EModalName } from '~/types/modals';
import type { IMainPageBannersData } from '~/types/banners';
import type { TActionLinkExternal, TActionLinkInternal, TActionLinkModal } from '~/types/links';

const props = defineProps({
  buttonAction: {
    type: Array as PropType<IMainPageBannersData['bannerAction']>,
    required: true,
  },
  isActionWrapper: {
    type: Boolean,
    default: false,
  },
});

const storeModals = useStoreModals();
const storeRoot = useStoreRoot();

const openLinkModal = (action: TActionLinkModal) => {
  if (action.modal) {
    let timeLag = 0;

    if (action.optionalRedirect) {
      openLinkInternal({ link: action.optionalRedirect });
      // crunch because we need some time for router changes
      timeLag = 1500;
    }

    setTimeout(() => {
      const modalToOpen = action.modal as unknown as EModalName;
      const modalsWithGtm = [EModalName.signup, EModalName.deposit];
      if (action.params && action.params.length) {
        const modalParams: { [key: string]: string } = {};

        action.params.forEach((p) => {
          if (p?.param && p?.value) {
            modalParams[p.param] = p.value;
          }
        });
        storeModals.openModal(
          modalToOpen,
          modalsWithGtm.includes(modalToOpen)
            ? { ...modalParams, gtmParams: { button_place: 'main_banner' } }
            : modalParams
        );
      } else {
        storeModals.openModal(
          modalToOpen,
          modalsWithGtm.includes(modalToOpen) ? { gtmParams: { button_place: 'main_banner' } } : {}
        );
      }
    }, timeLag);
  }
};

const openLinkInternal = ({ link }: Partial<TActionLinkInternal>) => {
  let path: string = link || '/';

  if (storeRoot.domainLang) {
    path = `/${storeRoot.domainLang}${path}`;
  }
  return navigateTo({ path });
};

const openLinkExternal = (action: TActionLinkExternal) => {
  let url: string = action.link || '/';
  if (!url.startsWith('http') && storeRoot.domainLang) {
    url = `/${storeRoot.domainLang}${url}`;
  }

  window.location.href = url;
};

const openLink = () => {
  if (props.buttonAction) {
    const action: TActionLinkExternal | TActionLinkInternal | TActionLinkModal = props
      .buttonAction[0] as any;
    switch (action.__component) {
      case 'link.modal': {
        openLinkModal(action);
        break;
      }
      case 'link.internal': {
        openLinkInternal(action);
        break;
      }
      case 'link.external': {
        openLinkExternal(action);
        break;
      }
      // no default
    }
  }
};
</script>
<style lang="scss" module>
.bannerButton {
  transition: none !important;
}
</style>
